import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { Button, Flex, Panel, PanelSection } from '@customerio/pluma-components/react';
import { ImportType } from '@/services/db/sql-import/entity';
import { QueryStep } from '../../new/components/query-step';
import { queryWriteBody } from '../edit';
import { useEditableQuery } from './use-editable-query';

/** The query review modal links here with `?runQuery=true`, so the saved query previews on arrival. */
export const RUN_QUERY_PARAM = 'runQuery';

/** The Query tab: the import's query, previewed against its database and saved with `update_query`. */
export function QueryTab() {
  const navigate = useNavigate();
  const { detail, edits, steps, save, isSaving } = useEditableQuery();
  const { type, objectTypes, canEdit, canIdentifyByEmail, hrefs } = detail;

  // Arriving from the query review modal previews the saved query once, then drops the param.
  const [runQuery, setRunQuery] = useQueryState(RUN_QUERY_PARAM, parseAsBoolean);
  const autoRan = useRef(false);
  useEffect(() => {
    if (runQuery === true && !autoRan.current) {
      autoRan.current = true;
      steps.runQuery();
      void setRunQuery(null);
    }
  }, [runQuery, setRunQuery, steps]);

  const isObjectImport = edits.draft.import_type === ImportType.object;
  const objectTypeName = isObjectImport
    ? objectTypes.find((objectType) => objectType.id === edits.draft.object_type_id)?.name
    : undefined;
  // Save waits for a preview to succeed, as Ember's did: a query that has not run is not proven.
  const canSave = canEdit && edits.isDirty && steps.hasSuccessfulQuery;

  return (
    <Panel>
      <PanelSection>
        <QueryStep
          type={type}
          draft={edits.draft}
          objectTypeName={objectTypeName}
          isNewImport={false}
          isReadOnly={!canEdit}
          canIdentifyByEmail={canIdentifyByEmail}
          run={steps.queryRun}
          onQueryChange={steps.setQuery}
          onLocationChange={steps.setLocation}
          onRun={steps.runQuery}
          onGoToSettings={() => void navigate({ href: hrefs.settings })}
          onTimeoutDismiss={() => steps.dismissTimeout(false)}
          onTimeoutContinue={() => steps.continueAfterTimeout(false)}
        />
        <Flex justifyContent="flex-end" mt="200">
          <Button
            variant="primary"
            isDisabled={!canSave}
            isLoading={isSaving}
            onClick={() => void save(edits.draft, queryWriteBody(edits.draft))}
            data-test-sql-import-button="submit"
          >
            Save changes
          </Button>
        </Flex>
      </PanelSection>
    </Panel>
  );
}
